import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { PatientHistoryType } from 'src/graphql.generated'
import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { PatientHistoryFormType, PATIENT_HISTORY_TYPE_NAMES } from 'src/participantHistory/constants'
import { useNoteDraftsQuery } from 'src/noteDraft/noteDraft.generated'
import Form from './form/Form'
import { getFormFromLocalStorage } from './form/persistFormHelpers'
import { useGetPatientHistoryTypes } from './useGetPatientHistoryTypes'

const ParticipantHistoryForm = ({ uid }: { uid: string }) => {
  const patientHistoryTypesResult = useGetPatientHistoryTypes(uid)
  const patientHistoryTypes = useMemo(
    () => (patientHistoryTypesResult || []) as PatientHistoryFormType[],
    [patientHistoryTypesResult]
  )
  const [formType, setFormType] = useState<PatientHistoryFormType>(() => getDefaultFormType(uid))
  const [userPickedType, setUserPickedType] = useState(false)
  const location = useLocation()
  const drafts = useNoteDraftsQuery({ variables: { uid }, fetchPolicy: 'cache-and-network' })
  const draftTypes = useMemo(() => new Set(drafts.data?.noteDrafts.map((draft) => draft.type)), [drafts.data])
  // The form opens only once the network draft list is in, and its type is picked from that result. The cached list is
  // not enough, since autosave never writes it. A later refetch must not switch away from a note that is being typed.
  const pickedFromDrafts = useRef<string | null>(null)
  const draftsSettled =
    pickedFromDrafts.current === uid || (!drafts.loading && (drafts.data !== undefined || drafts.error !== undefined))

  useEffect(() => {
    if (!draftsSettled || pickedFromDrafts.current === uid) {
      return
    }
    pickedFromDrafts.current = uid
    const urlFormType = new URLSearchParams(location.search).get('formType')
    if (urlFormType || userPickedType) {
      return
    }
    setFormType(getDefaultFormType(uid, drafts.data?.noteDrafts.map((draft) => draft.type) ?? []))
  }, [drafts.data, draftsSettled, location.search, uid, userPickedType])

  // Check for formType in URL parameters and update if present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlFormType = searchParams.get('formType')

    if (urlFormType && patientHistoryTypes.includes(urlFormType as PatientHistoryFormType)) {
      setFormType(urlFormType as PatientHistoryFormType)
    }
  }, [location.search, patientHistoryTypes])

  const handleTypeChange = (event: SelectChangeEvent<PatientHistoryType | 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN'>) => {
    const type = event.target.value
    setUserPickedType(true)
    setFormType(type as PatientHistoryFormType)
  }

  return (
    <Card sx={{ overflow: 'clip' }}>
      <CardContent>
        <FormControl>
          <InputLabel id="participant-history-select-label">Type</InputLabel>
          <Select
            labelId="participant-history-select-label"
            value={formType}
            label="Type"
            onChange={handleTypeChange}
            size="small"
            renderValue={(value) => PATIENT_HISTORY_TYPE_NAMES[value as PatientHistoryFormType]}
          >
            {patientHistoryTypes.map((field) => (
              <MenuItem key={field} value={field}>
                {PATIENT_HISTORY_TYPE_NAMES[field]}
                {draftTypes.has(field) && <Chip label="Draft" size="small" sx={{ ml: 1 }} />}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {draftsSettled ? (
          <Form uid={uid} formType={formType} setFormType={setFormType} />
        ) : (
          <CircularProgress size={24} sx={{ display: 'block', mt: 2 }} />
        )}
      </CardContent>
    </Card>
  )
}

const PREFERRED_DRAFT_TYPES = [
  PatientHistoryType.IntakeNote,
  PatientHistoryType.IntakeNotePsychiatry,
  PatientHistoryType.IntakeNoteComprehensive,
] as const

const getDefaultFormType = (uid: string, serverTypes: string[] = []) => {
  const match = PREFERRED_DRAFT_TYPES.find((type) => serverTypes.includes(type) || getFormFromLocalStorage(uid, type))
  return match ?? PatientHistoryType.Note
}

export default ParticipantHistoryForm
